import clsx from "clsx";

import { ListSelection } from "common/components/Filter";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { LABEL_COLORS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useAllLabels from "common/datahooks/useAllLabels";
import useLeadsFacet from "common/datahooks/useLeadsFacet";
import useSortedFacets from "common/hooks/useSortedFacets";
import type {
  LeadsFacets,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

const loaderClassNames = ["w-48", "w-36", "w-40", "w-52", "w-36", "w-48"];

export default function SearchFacet({
  facetKey,
  searchParams,
  searchValue,
  selectedFacets,
  toggleSearchParam,
}: {
  facetKey: keyof LeadsFacets;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  searchValue: string;
  selectedFacets: SelectedFacetParam[];
  toggleSearchParam: (facetParam: SelectedFacetParam) => void;
}) {
  const { facetData, isFetchingFacet } = useLeadsFacet(facetKey, searchParams);
  const { accounts } = useSelectedWorkspaceContext();
  const { allLabels } = useAllLabels("workspace");

  const sortedFacets = useSortedFacets(facetData || [], selectedFacets);

  const filteredFacets = sortedFacets.filter(({ id, name }) => {
    const isSelected = !!selectedFacets.find(
      (selectedFacet) => id === selectedFacet.id,
    );
    const facetLabel =
      facetKey === "lead_of"
        ? accounts.find((account) => account.id === id).full_name
        : name;
    return (
      (!searchValue ||
        facetLabel.toLowerCase().includes(searchValue.toLowerCase())) &&
      !isSelected
    );
  });

  const isLoading = !facetData && isFetchingFacet;
  const options = isLoading
    ? new Array(loaderClassNames.length).fill(undefined).map((_, index) => ({
        id: index.toString(),
        name: "",
        target_count: 0,
      }))
    : filteredFacets;

  return (
    <ListSelection
      countKey="target_count"
      options={options}
      onSelect={(selectedOption) => {
        toggleSearchParam(selectedOption);
      }}
      isDisabled={isFetchingFacet}
      renderLabel={({ name, id }) => {
        if (isLoading) {
          return <Skeleton className={`${loaderClassNames[Number(id)]} h-5`} />;
        }
        if (facetKey === "lead_of") {
          const account = accounts.find(({ id }) => id === name);
          return account.full_name;
        }
        return name;
      }}
      renderIcon={(facet) => {
        if (facetKey === "lead_of") {
          if (isLoading) {
            return <Skeleton className="rounded-full size-6" />;
          }
          const account = accounts.find(({ id }) => id === facet.id);
          return <ProfileImage size="2xs" src={account.picture_url} />;
        }
        if (facetKey === "labels") {
          if (isLoading) {
            return <Skeleton className="m-0.5 size-3.5 rounded" />;
          }
          const label = allLabels.find(({ id }) => id === facet.id);

          if (label) {
            return (
              <div
                className={clsx(
                  "m-0.5 size-3.5 rounded",
                  LABEL_COLORS[label.color].bgClassName,
                )}
              />
            );
          }
        }
      }}
    />
  );
}
